import { useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { LoadingState, useToast } from '@radiology/ui';
import { DocumentDownload } from 'iconsax-react';
import dayjs from 'dayjs';
import { STATUS_TONE, getFileUrl, useCertificates, type Certificate } from './api';

/**
 * Every version of one certificate type, oldest change at the bottom.
 *
 * The CR asks for a history that keeps "certificate type, upload date, expiry
 * date, status, uploaded file, review date, reviewer, rejection reason" and
 * says historical versions are read-only. So this dialog has no actions on the
 * rows beyond opening the file — the current version is acted on from the
 * requirement row, and nothing here can be edited or removed.
 *
 * All of it comes from `certificates` filtered by name. There is no separate
 * history table because renewal never overwrote anything in the first place: a
 * renewal INSERTs, so the history is simply the rows that are already there.
 */
export function HistoryDialog({
  certificateType,
  onClose,
}: {
  certificateType: string | null;
  onClose: () => void;
}) {
  const { data, isLoading } = useCertificates();
  const toast = useToast();

  const versions = useMemo(() => {
    if (!certificateType) return [];
    const key = certificateType.trim().toLowerCase();
    return (
      (data ?? [])
        .filter(c => c.name.trim().toLowerCase() === key)
        // Newest first — the current version is what people look for.
        .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
    );
  }, [data, certificateType]);

  const openFile = async (fileName: string) => {
    try {
      window.open(await getFileUrl(fileName), '_blank', 'noopener');
    } catch {
      toast.error('That file could not be opened. It may not have been migrated.');
    }
  };

  // The version that decides the requirement's status: the approved one
  // reaching furthest into the future. Same rule as the database view, so the
  // badge here cannot disagree with the dashboard.
  const currentId = useMemo(() => {
    const approved = versions.filter(v => ['valid', 'expiring soon', 'expired'].includes(v.status));
    if (approved.length === 0) return null;
    return approved.reduce((best, v) => (v.expiry_date > best.expiry_date ? v : best)).id;
  }, [versions]);

  return (
    <Dialog open={Boolean(certificateType)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle variant="h4">{certificateType}</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <LoadingState variant="skeleton" rows={3} />
        ) : versions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            You have not uploaded this certificate yet.
          </Typography>
        ) : (
          <Stack gap={0.5}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {versions.length} {versions.length === 1 ? 'version' : 'versions'} on record. Earlier
              versions are kept and cannot be changed.
            </Typography>
            {versions.map(version => (
              <Version
                key={version.id}
                version={version}
                isCurrent={version.id === currentId}
                onOpen={() => void openFile(version.file_name)}
              />
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function Version({
  version,
  isCurrent,
  onOpen,
}: {
  version: Certificate;
  isCurrent: boolean;
  onOpen: () => void;
}) {
  const tone = STATUS_TONE[version.status];
  // The supervisor's last word on this version. A rejection must carry one, so
  // this is where the reason for a rejected version lives.
  const lastComment = version.comment?.[version.comment.length - 1];

  return (
    <>
      <Stack direction="row" alignItems="flex-start" gap={1.5} sx={{ py: 1.25 }}>
        <Stack sx={{ flex: 1, minWidth: 0 }} gap={0.5}>
          <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
            <Chip
              size="small"
              label={version.status}
              color={tone === 'default' ? undefined : tone}
              variant={tone === 'default' ? 'outlined' : 'filled'}
              sx={{ textTransform: 'capitalize' }}
            />
            {isCurrent && <Chip size="small" variant="outlined" label="Current" />}
          </Stack>

          <Typography variant="body2">
            Expires {dayjs(version.expiry_date).format('D MMM YYYY')}
          </Typography>

          <Typography variant="caption" color="text.secondary">
            Uploaded {dayjs(version.created_at).format('D MMM YYYY')}
            {version.approved_date
              ? ` · reviewed ${dayjs(version.approved_date).format('D MMM YYYY')}`
              : ''}
            {lastComment?.authorName ? ` by ${lastComment.authorName}` : ''}
          </Typography>

          {lastComment?.comment && (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              “{lastComment.comment}”
            </Typography>
          )}
        </Stack>

        <Tooltip title="Open this version">
          <IconButton size="small" onClick={onOpen}>
            <DocumentDownload size={18} color="currentColor" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Divider />
    </>
  );
}
